import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Carousel,
  DrilldownModal,
  ExportButtonGroup,
  KpiCard,
  Tabs,
  useExport,
  useToast,
} from '@kss/ui';
import { useHasRole } from '@kss/auth';
import { useExportGate } from '../../hooks/useExportGate';
import {
  Boxes,
  CheckCircle2,
  XCircle,
  Tag,
  IndianRupee,
  Layers,
  Store as StoreIcon,
  RefreshCw,
  Lock,
  Search,
  X as XIcon,
  Printer,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Product } from '@kss/api';
import { productService } from '@kss/api';

/* -------------------------------------------------------------------- */
/* KPI tab                                                               */
/* -------------------------------------------------------------------- */

type KpiId =
  | 'total'
  | 'active'
  | 'inactive'
  | 'uniqueSku'
  | 'avgPrice'
  | 'maxPrice'
  | 'categories'
  | 'stores';

interface KpiDef {
  id: KpiId;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  compute: (rows: Product[]) => { value: string | number };
  detail: (rows: Product[]) => Product[];
}

const inr = (n: number) =>
  `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const KPIS: KpiDef[] = [
  {
    id: 'total',
    title: 'Total products',
    icon: <Boxes className="h-5 w-5 text-primary" />,
    compute: (r) => ({ value: r.length }),
    detail: (r) => r,
  },
  {
    id: 'active',
    title: 'Active',
    icon: <CheckCircle2 className="h-5 w-5 text-success" />,
    compute: (r) => ({ value: r.filter((p) => p.isActive).length }),
    detail: (r) => r.filter((p) => p.isActive),
  },
  {
    id: 'inactive',
    title: 'Inactive',
    icon: <XCircle className="h-5 w-5 text-danger" />,
    compute: (r) => ({ value: r.filter((p) => !p.isActive).length }),
    detail: (r) => r.filter((p) => !p.isActive),
  },
  {
    id: 'uniqueSku',
    title: 'Unique SKUs',
    icon: <Tag className="h-5 w-5 text-info" />,
    compute: (r) => ({
      value: new Set(r.map((p) => p.sku).filter(Boolean)).size,
    }),
    detail: (r) => {
      const seen = new Set<string>();
      return r.filter((p) => {
        if (!p.sku || seen.has(p.sku)) return false;
        seen.add(p.sku);
        return true;
      });
    },
  },
  {
    id: 'avgPrice',
    title: 'Avg price',
    icon: <IndianRupee className="h-5 w-5 text-warning" />,
    compute: (r) => {
      if (!r.length) return { value: inr(0) };
      const total = r.reduce((s, p) => s + Number(p.sellingPrice || 0), 0);
      return { value: inr(total / r.length) };
    },
    detail: (r) =>
      [...r].sort((a, b) => Number(b.sellingPrice || 0) - Number(a.sellingPrice || 0)),
  },
  {
    id: 'maxPrice',
    title: 'Highest price',
    icon: <IndianRupee className="h-5 w-5 text-primary" />,
    compute: (r) => ({
      value: inr(r.reduce((m, p) => Math.max(m, Number(p.sellingPrice || 0)), 0)),
    }),
    detail: (r) =>
      [...r]
        .sort((a, b) => Number(b.sellingPrice || 0) - Number(a.sellingPrice || 0))
        .slice(0, 20),
  },
  {
    id: 'categories',
    title: 'Categories',
    subtitle: 'Distinct',
    icon: <Layers className="h-5 w-5 text-info" />,
    compute: (r) => ({
      value: new Set(r.map((p) => p.categoryId).filter(Boolean)).size,
    }),
    detail: (r) => {
      const seen = new Set<string>();
      return r.filter((p) => {
        const k = p.categoryId ?? '';
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    },
  },
  {
    id: 'stores',
    title: 'Stores',
    subtitle: 'Distinct',
    icon: <StoreIcon className="h-5 w-5 text-success" />,
    compute: (r) => ({
      value: new Set(r.map((p) => p.storeId).filter(Boolean)).size,
    }),
    detail: (r) => {
      const seen = new Set<string>();
      return r.filter((p) => {
        const k = p.storeId ?? '';
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    },
  },
];

/* -------------------------------------------------------------------- */
/* Drilldown table                                                       */
/* -------------------------------------------------------------------- */

const DRILL_COLUMNS: { key: string; header: string }[] = [
  { key: 'sku', header: 'SKU' },
  { key: 'name', header: 'Name' },
  { key: 'sellingPrice', header: 'Price (₹)' },
  { key: 'isActive', header: 'Status' },
  { key: 'categoryId', header: 'Category ID' },
  { key: 'storeId', header: 'Store ID' },
];

function DrillTable({ rows }: { rows: Product[] }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-content-muted">
        No records for this slice.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            {DRILL_COLUMNS.map((c) => (
              <th
                key={c.key}
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted"
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr
              key={(p.productId ?? i) + ''}
              className="border-b border-border/60 hover:bg-surface-card-hover"
            >
              <td className="px-3 py-2 font-mono text-xs">{p.sku}</td>
              <td className="px-3 py-2">{p.name}</td>
              <td className="px-3 py-2 text-right">
                {Number(p.sellingPrice ?? 0).toLocaleString('en-IN')}
              </td>
              <td className="px-3 py-2">
                <span
                  className={
                    p.isActive
                      ? 'inline-flex rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success'
                      : 'inline-flex rounded-full bg-danger/15 px-2 py-0.5 text-xs font-medium text-danger'
                  }
                >
                  {p.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-3 py-2 font-mono text-[11px] text-content-muted">
                {p.categoryId ?? '—'}
              </td>
              <td className="px-3 py-2 font-mono text-[11px] text-content-muted">
                {p.storeId ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Theme-aware chart colour palette                                      */
/* -------------------------------------------------------------------- */

function useThemeChartColors(count = 8): string[] {
  const [colors, setColors] = useState<string[]>([]);

  useEffect(() => {
    const read = () => {
      const root = document.documentElement;
      const out: string[] = [];
      for (let i = 1; i <= count; i++) {
        const c = getComputedStyle(root).getPropertyValue(`--chart-${i}`).trim();
        if (c) out.push(c);
      }
      setColors(
        out.length
          ? out
          : [
              '#3b82f6',
              '#8b5cf6',
              '#ec4899',
              '#f59e0b',
              '#22c55e',
              '#06b6d4',
              '#f43f5e',
              '#a855f7',
            ]
      );
    };
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'style'],
    });
    return () => obs.disconnect();
  }, [count]);

  return colors;
}

/* -------------------------------------------------------------------- */
/* Chart panels                                                          */
/* -------------------------------------------------------------------- */

interface DrillContext {
  title: string;
  subtitle?: string;
  rows: Product[];
}

function StatusDonut({
  rows,
  onSlice,
  colors,
}: {
  rows: Product[];
  onSlice: (ctx: DrillContext) => void;
  colors: string[];
}) {
  const data = useMemo(() => {
    const active = rows.filter((p) => p.isActive);
    const inactive = rows.filter((p) => !p.isActive);
    return [
      { name: 'Active', value: active.length, detail: active },
      { name: 'Inactive', value: inactive.length, detail: inactive },
    ];
  }, [rows]);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={90}
          paddingAngle={2}
          onClick={(e: { payload?: { name: string; detail: Product[] } }) => {
            if (!e?.payload) return;
            onSlice({
              title: `Products by status — ${e.payload.name}`,
              subtitle: `${e.payload.detail.length} row(s)`,
              rows: e.payload.detail,
            });
          }}
          className="cursor-pointer"
        >
          {data.map((d, i) => (
            <Cell key={d.name} fill={colors[i % colors.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            fontSize: 12,
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: 11 }}
          formatter={(v) => (
            <span style={{ color: 'var(--color-text-secondary)' }}>{v}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function GroupedBar({
  rows,
  groupBy,
  label,
  onBar,
  colors,
}: {
  rows: Product[];
  groupBy: 'categoryId' | 'storeId';
  label: string;
  onBar: (ctx: DrillContext) => void;
  colors: string[];
}) {
  const data = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const p of rows) {
      const key = (p[groupBy] as string) || 'Unassigned';
      const list = map.get(key) ?? [];
      list.push(p);
      map.set(key, list);
    }
    return [...map.entries()]
      .map(([key, list]) => ({
        name: key.length > 14 ? `${key.slice(0, 8)}…${key.slice(-4)}` : key,
        fullKey: key,
        count: list.length,
        detail: list,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [rows, groupBy]);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart
        data={data}
        margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
        onClick={(e: { activePayload?: Array<{ payload: (typeof data)[number] }> }) => {
          const p = e?.activePayload?.[0]?.payload;
          if (!p) return;
          onBar({
            title: `${label} — ${p.fullKey}`,
            subtitle: `${p.detail.length} row(s)`,
            rows: p.detail,
          });
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis
          dataKey="name"
          tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            fontSize: 12,
          }}
          cursor={{ fill: 'var(--color-bg-card-hover)' }}
        />
        <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40} className="cursor-pointer">
          {data.map((_, i) => (
            <Cell key={i} fill={colors[i % colors.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* -------------------------------------------------------------------- */
/* Main dashboard                                                        */
/* -------------------------------------------------------------------- */

const CARDS_PER_SLIDE = 4;
type TabId = 'kpis' | 'charts';

export default function ProductDashboard() {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const canView = useHasRole(['admin', 'manager', 'staff']);
  const exportGate = useExportGate(['admin', 'manager']);
  const canExport = exportGate.enabled;

  const [rows, setRows] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('kpis');
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [openKpi, setOpenKpi] = useState<KpiDef | null>(null);
  const [chartDrill, setChartDrill] = useState<DrillContext | null>(null);

  const { exportToExcel, exportToPdf, printWidget } = useExport();
  const drillBodyRef = useRef<HTMLDivElement>(null);
  const chartDrillBodyRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);

  const colors = useThemeChartColors();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await productService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load products';
      setError(msg);
      toastRef.current.error('Load failed', msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (p) =>
        (p.name ?? '').toLowerCase().includes(q) ||
        (p.sku ?? '').toLowerCase().includes(q) ||
        (p.categoryId ?? '').toLowerCase().includes(q) ||
        (p.storeId ?? '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  const kpis = useMemo(
    () => KPIS.map((def) => ({ def, ...def.compute(filteredRows) })),
    [filteredRows]
  );

  const kpiSlides = useMemo(() => {
    const chunks: (typeof kpis)[] = [];
    for (let i = 0; i < kpis.length; i += CARDS_PER_SLIDE) {
      chunks.push(kpis.slice(i, i + CARDS_PER_SLIDE));
    }
    return chunks.map((chunk, idx) => ({
      id: idx,
      content: (
        <div className="grid h-full grid-cols-1 gap-3 p-1 sm:grid-cols-2 lg:grid-cols-4">
          {chunk.map(({ def, value }) => (
            <KpiCard
              key={def.id}
              title={def.title}
              subtitle={def.subtitle}
              value={value}
              icon={def.icon}
              onClick={() => setOpenKpi(def)}
            />
          ))}
        </div>
      ),
    }));
  }, [kpis]);

  const rowsToExcel = (list: Product[]) =>
    list.map((p) => ({
      sku: p.sku,
      name: p.name,
      sellingPrice: p.sellingPrice,
      isActive: p.isActive ? 'Active' : 'Inactive',
      categoryId: p.categoryId ?? '',
      storeId: p.storeId ?? '',
    }));

  const kpiDrillRows = openKpi ? openKpi.detail(filteredRows) : [];
  const kpiExcel = () => {
    if (!openKpi) return;
    exportToExcel(rowsToExcel(kpiDrillRows), DRILL_COLUMNS, `products-${openKpi.id}`);
  };
  const kpiPdf = () => {
    if (!openKpi || !drillBodyRef.current) return;
    exportToPdf(drillBodyRef.current, `products-${openKpi.id}`);
  };

  const chartExcel = () => {
    if (!chartDrill) return;
    exportToExcel(
      rowsToExcel(chartDrill.rows),
      DRILL_COLUMNS,
      `products-${chartDrill.title.replace(/\s+/g, '-').toLowerCase()}`
    );
  };
  const chartPdf = () => {
    if (!chartDrill || !chartDrillBodyRef.current) return;
    exportToPdf(
      chartDrillBodyRef.current,
      chartDrill.title.replace(/\s+/g, '-').toLowerCase()
    );
  };

  const printDashboard = async () => {
    if (!dashboardRef.current) return;
    setPrinting(true);
    try {
      await exportToPdf(dashboardRef.current, 'products-dashboard');
    } finally {
      setPrinting(false);
    }
  };

  if (!canView) {
    return (
      <div className="-m-6 p-6">
        <div className="mx-auto mt-12 max-w-md rounded-xl border border-border bg-surface-card p-8 text-center">
          <Lock className="mx-auto h-8 w-8 text-content-muted" />
          <h2 className="mt-3 text-lg font-semibold text-content-primary">
            Access denied
          </h2>
          <p className="mt-1 text-sm text-content-muted">
            Your role does not permit viewing the Products dashboard.
          </p>
        </div>
      </div>
    );
  }

  const kpisContent = (
    <div className="space-y-3">
      {kpiSlides.length > 0 && (
        <Carousel
          slides={kpiSlides}
          autoPlay={false}
          showArrows
          showDots
          height={200}
        />
      )}
    </div>
  );

  const chartsContent = (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">
            By status
          </h3>
          <span className="text-[11px] text-content-muted">Click a slice</span>
        </div>
        <StatusDonut
          rows={filteredRows}
          onSlice={setChartDrill}
          colors={colors}
        />
      </Card>
      <Card>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">
            Top categories
          </h3>
          <span className="text-[11px] text-content-muted">Click a bar</span>
        </div>
        <GroupedBar
          rows={filteredRows}
          groupBy="categoryId"
          label="Products by category"
          onBar={setChartDrill}
          colors={colors}
        />
      </Card>
      <Card className="lg:col-span-2">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">
            Top stores
          </h3>
          <span className="text-[11px] text-content-muted">Click a bar</span>
        </div>
        <GroupedBar
          rows={filteredRows}
          groupBy="storeId"
          label="Products by store"
          onBar={setChartDrill}
          colors={colors}
        />
      </Card>
    </div>
  );

  const tabs = [
    { id: 'kpis' as const, label: 'KPI Overview', content: kpisContent },
    { id: 'charts' as const, label: 'Charts', content: chartsContent },
  ];

  const activeContent = tabs.find((t) => t.id === tab)?.content;

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface-card px-4 py-3">
        {searchOpen ? (
          <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-primary bg-surface-card px-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-content-muted" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, SKU, category or store..."
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchOpen(false);
                  setQuery('');
                }
              }}
              className="min-w-0 flex-1 bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted"
            />
            <button
              type="button"
              aria-label="Close search"
              onClick={() => {
                setSearchOpen(false);
                setQuery('');
              }}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-content-muted hover:bg-surface-card-hover hover:text-content-primary"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-content-primary">
            Products dashboard
          </h2>
        )}

        <div className="flex shrink-0 items-center gap-2">
          {!searchOpen && (
            <button
              type="button"
              aria-label="Open search"
              onClick={() => setSearchOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-content-muted hover:bg-surface-card-hover hover:text-content-primary"
            >
              <Search className="h-4 w-4" />
            </button>
          )}
          {canExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={printDashboard}
              disabled={printing}
              title="Print dashboard to PDF"
            >
              <Printer className={`h-3.5 w-3.5 ${printing ? 'animate-pulse' : ''}`} />
              {printing ? 'Printing…' : 'Print'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div ref={dashboardRef} className="space-y-3 bg-surface-page p-[3px]">
        <Tabs
          tabs={tabs}
          activeId={tab}
          onChange={(id) => setTab(id as TabId)}
          barOnly
        />
        <div>{activeContent}</div>
      </div>

      {/* KPI drilldown */}
      <DrilldownModal
        isOpen={!!openKpi}
        onClose={() => setOpenKpi(null)}
        title={openKpi ? `${openKpi.title} — details` : ''}
        subtitle={openKpi ? `${kpiDrillRows.length} row(s)` : ''}
        size="xl"
        actions={
          canExport ? (
            <ExportButtonGroup
              onExcel={kpiExcel}
              onPdf={kpiPdf}
              onPrint={printWidget}
              compact
            />
          ) : null
        }
      >
        <div ref={drillBodyRef}>
          <DrillTable rows={kpiDrillRows} />
          {!canExport && kpiDrillRows.length > 0 && (
            <p className="mt-3 text-[11px] text-content-muted">{exportGate.message}</p>
          )}
        </div>
      </DrilldownModal>

      {/* Chart drilldown */}
      <DrilldownModal
        isOpen={!!chartDrill}
        onClose={() => setChartDrill(null)}
        title={chartDrill?.title ?? ''}
        subtitle={chartDrill?.subtitle}
        size="xl"
        actions={
          canExport ? (
            <ExportButtonGroup
              onExcel={chartExcel}
              onPdf={chartPdf}
              onPrint={printWidget}
              compact
            />
          ) : null
        }
      >
        <div ref={chartDrillBodyRef}>
          <DrillTable rows={chartDrill?.rows ?? []} />
          {!canExport && (chartDrill?.rows.length ?? 0) > 0 && (
            <p className="mt-3 text-[11px] text-content-muted">{exportGate.message}</p>
          )}
        </div>
      </DrilldownModal>
    </div>
  );
}
